import { useContext, useState } from 'react';

import ConfirmDeleteModal from '../ConfirmDeleteModal';
import { ROOT_FOLDER_ID } from '../constants';
import CreateMediaFolderModal from '../CreateMediaFolderModal';
import EditMediaFolderModal from '../EditMediaFolderModal';
import FilesTabContext from '../FilesTabContext';
import MoveMediaFolderModal from '../MoveMediaFolderModal';
import { FolderModalType, FolderTreeOpenModal } from '../types';

import FolderTreeContext from './FolderTreeContext';
import FolderTreeView from './FolderTreeView';

type FolderTreeProps = {
  expandedIds: Set<string>;
  onToggleExpand: (id: string) => void;
};

const FolderTree = ({ expandedIds, onToggleExpand }: FolderTreeProps) => {
  const { canUpdate, canDelete, createFolder, editFolder, deleteFolder } =
    useContext(FilesTabContext);

  const [modal, setModal] = useState<FolderTreeOpenModal>({
    type: FolderModalType.NONE,
  });

  const handleConfirmDelete = () => {
    if (modal.type === FolderModalType.DELETE) {
      deleteFolder(modal.id);
    }

    setModal({ type: FolderModalType.NONE });
  };

  const contextValue = {
    expandedIds,
    onToggleExpand,
    openModal: setModal,
  };

  return (
    <FolderTreeContext.Provider value={contextValue}>
      <FolderTreeView />

      {canUpdate && (
        <EditMediaFolderModal
          open={modal.type === FolderModalType.EDIT}
          folderId={modal.type === FolderModalType.EDIT ? modal.id : ''}
          initialName={modal.type === FolderModalType.EDIT ? modal.name : ''}
          onEdit={editFolder}
          closeModal={() => setModal({ type: FolderModalType.NONE })}
        />
      )}
      <CreateMediaFolderModal
        open={modal.type === FolderModalType.CREATE_SUB}
        parentFolderId={
          modal.type === FolderModalType.CREATE_SUB ? modal.id : ''
        }
        onCreate={createFolder}
        closeModal={() => setModal({ type: FolderModalType.NONE })}
      />
      {canUpdate && modal.type === FolderModalType.MOVE && (
        <MoveMediaFolderModal
          open
          type="folder"
          ids={[modal.id]}
          closeModal={() => setModal({ type: FolderModalType.NONE })}
          disabled={[
            modal.id,
            modal.ancestors.length > 1
              ? modal.ancestors[modal.ancestors.length - 2]
              : ROOT_FOLDER_ID,
          ]}
        />
      )}
      {canDelete && (
        <ConfirmDeleteModal
          open={modal.type === FolderModalType.DELETE}
          title={
            modal.type === FolderModalType.DELETE
              ? `Delete ${modal.name} folder`
              : 'Delete'
          }
          onCancel={() => setModal({ type: FolderModalType.NONE })}
          onConfirm={handleConfirmDelete}
        >
          Are you sure you want to delete this folder? If the folder has any
          subfolders or files saved below this folder, everything will be
          deleted.
        </ConfirmDeleteModal>
      )}
    </FolderTreeContext.Provider>
  );
};

export default FolderTree;
